import React, { useState, useEffect, useCallback, useRef } from 'react';
import Helmet from 'react-helmet';
import { usePage } from '../contexts/pageContext';
import styled, { css } from 'styled-components/macro';
import { NotificationButton } from '../components/NotificationButton';
import Descarga from '../components/svg/Descarga';
import { useTranslate } from '../contexts/languageContext';
import ResultadosComposicion from '../containers/ResultadosComposicion';
import ResultadosVotosPartidos from '../containers/ResultadosVotosPartido';
import ResultadosInfoGeneral from '../containers/ResultadosInfoGeneral';
import ResultadosTerritorios from '../containers/ResultadosTerritorios';
import ResultadosVotosConsultas from '../containers/ResultadosVotosConsultas';
import { useEleccion, useSetEleccion } from '../contexts/eleccionContext';
import { responsive } from '../resources/constantes.json';
import { useIsDesktop, useIsMobile } from '../hooks/useMediaQuery';
import { usePrintEvent } from '../print/Print';

const Resultados = ({ nomenclator }) => {
  const printEvent = usePrintEvent();
  const pageState = usePage();
  const elecOptions = useEleccion();
  const setElecOptions = useSetEleccion();
  const t = useTranslate();
  const { data, urlInfo } = pageState;
  const { eleccion, idHistorico } = urlInfo;
  const nomAmbitos = nomenclator.nomAmbitos[eleccion];
  const esHistorico = idHistorico != null;
  const ColorHeader = esHistorico ? '#646464' : '#1C283D';
  const isDesktop = useIsDesktop();
  const isMobile = useIsMobile();
  const Color = isMobile ? nomAmbitos.cs : '#222';

  const printPage = () => {
    printEvent();
  };

  const getSelectedSection = tab => {
    switch (tab.id) {
      case 'composicion':
        return <ResultadosComposicion nomenclator={nomenclator} />;
      case 'votosPartidos':
        return <ResultadosVotosPartidos />;
      case 'votosPartidosConsultas':
        return <ResultadosVotosConsultas />;
      case 'territorios':
        return <ResultadosTerritorios />;
      case 'infoGeneral':
        return <ResultadosInfoGeneral />;
      default:
        return <></>;
    }
  };

  const getTitle = tabId => {
    switch (tabId) {
      case 'composicion':
        return <StrongText> {t(elecOptions.selectedTab.id)}</StrongText>;
      case 'votosPartidos':
        return (
          <p>
            Votos a <StrongText>Partidos</StrongText>
          </p>
        );
      case 'votosPartidosConsultas':
        return (
          <p>
            Votos a <StrongText>Pre-candidatos</StrongText>
          </p>
        );
      case 'territorios':
        return (
          <p>
            Resumen por <StrongText>Territorios</StrongText>
          </p>
        );
      case 'infoGeneral':
        return (
          <p>
            Información <StrongText>General</StrongText>
          </p>
        );
      default:
        return <></>;
    }
  };
  return (
    <Main esHistorico={esHistorico}>
      <Helmet>
        <title itemProp="name" lang="es">
          {nomAmbitos.n}
        </title>
      </Helmet>
      <>
        {!isMobile ? (
          <ContainerCabecera color={ColorHeader}>
            <TitleSection>
              {elecOptions && getTitle(elecOptions.selectedTab.id)}
            </TitleSection>
            <ContainerSelectors>
              {elecOptions &&
                elecOptions.tabs.map((tab, i) => {
                  return (
                    <ButtonSelector
                      buttonActivated={tab.id === elecOptions.selectedTab.id}
                      onClick={() =>
                        setElecOptions({ ...elecOptions, selectedTab: tab })
                      }
                      title={tab.title}
                      nomAmbitos={nomAmbitos}
                    >
                      {tab.element({
                        fill:
                          tab.id === elecOptions.selectedTab.id
                            ? '#00F7F0'
                            : '#fff',
                      })}
                    </ButtonSelector>
                  );
                })}
            </ContainerSelectors>
            <SubConatinerRight>
              <MesasInformadasTitle>
                Mesas informadas
                <MesasInformadasNumero>
                  {data.totales.act.pmesesc}
                </MesasInformadasNumero>
              </MesasInformadasTitle>
              {isDesktop && (
                <SCajaDescargas>
                  <StyledA
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => printPage()}
                  >
                    <Descarga width={30} height={30} fill="#1C4183" />
                    <div className="visually-hidden">{t('descargarEnPdf')}</div>
                  </StyledA>
                </SCajaDescargas>
              )}
              {!esHistorico && (
                <SCajaNotificacion>
                  <NotificationButton nomenclator={nomenclator} />
                </SCajaNotificacion>
              )}
            </SubConatinerRight>
          </ContainerCabecera>
        ) : (
          <div>
            <ContainerSelectors>
              {elecOptions &&
                elecOptions.tabs.map((tab, i) => {
                  return (
                    <ButtonSelector
                      buttonActivated={tab.id === elecOptions.selectedTab.id}
                      onClick={() =>
                        setElecOptions({ ...elecOptions, selectedTab: tab })
                      }
                      title={tab.title}
                      nomAmbitos={nomAmbitos}
                      key={i}
                    >
                      {tab.element({
                        fill:
                          tab.id === elecOptions.selectedTab.id
                            ? '#112F50'
                            : '#B1B1B1',
                      })}
                    </ButtonSelector>
                  );
                })}
            </ContainerSelectors>
            <TitleSectionSelected>
              <NombreSection>
                {elecOptions && t(elecOptions.selectedTab.id)}
              </NombreSection>
            </TitleSectionSelected>
          </div>
        )}
      </>
      <ContainerSection>
        {elecOptions && getSelectedSection(elecOptions.selectedTab)}
      </ContainerSection>
    </Main>
  );
};

const StrongText = styled.span`
  font-weight: 500;
`;

const MesasInformadasTitle = styled.p`
  margin-right: 2rem;
  font-size: 18px;
  font-weight: 100;
  text-align: left;
  box-sizing: border-box;
  @media (max-width: ${responsive.tablet}px) {
    font-size: 16px;
  }
`;

const MesasInformadasNumero = styled.span`
  font-weight: 400;
  font-size: 24px;
  margin-left: 5px;
  @media (max-width: ${responsive.tablet}px) {
    font-size: 18px;
  }
`;
const Main = styled(({ esHistorico, ...rest }) => <div {...rest} />)`
  display: block;
  width: 100%;
  height: 100%;
  max-width: 1366px;
  margin: auto;
  margin-top: 0;
  margin-bottom: 0;
  @media (max-width: ${responsive.tablet}px) {
    margin-top: ${({ esHistorico }) => (esHistorico ? '0px' : '124px')};
  }
  @media (max-width: ${responsive.mobile}px) {
    margin-top: 153px;
  }
`;

const ContainerCabecera = styled(({ color, ...rest }) => <div {...rest} />)`
  display: flex;
  color: #fff;
  justify-content: space-between;
  border-top: 1px solid #00f7f0;
  width: 100%;
  margin: auto;
  height: 44px;
  padding-top: 5px;
  padding-bottom: 5px;
  align-items: center;
  background:  ${({ color }) => color};
  @media (max-width: ${responsive.mobile}px) {
    background: transparent;
  }
}
`;

const ContainerSection = styled.div`
  background: #fff;
  width: 100%;
  margin: auto;
  height: 80%;
  @media (max-width: ${responsive.mobile}px) {
    background: white;
  }
`;

const SubConatinerRight = styled.div`
  flex-direction: row;
  display: flex;
  width: 34%;
  justify-content: flex-end;
  align-items: center;
`;

const ContainerSelectors = styled.div`
  width: 30%;
  display: flex;
  justify-content: center;
  @media (max-width: ${responsive.mobile}px) {
    width: 100%;
    justify-content: space-around;
    background: #ededed;
  }
`;

const TitleSection = styled.h2`
  width: 34%;
  font-size: 26px;
  font-weight: 100;
  letter-spacing: 0em;
  text-align: left;
  padding-left: 10px;
  @media (max-width: ${responsive.tablet}px) {
    font-size: 22px;
  }
`;

const ButtonSelector = styled(({ buttonActivated, nomAmbitos, ...rest }) => (
  <button {...rest} />
))`
  margin-left: 5px;
  margin-right: 5px;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  align-items: center;
  padding: 10px;
  border-radius: 20px;
  width: 44px;
  height: 44px;
  background: 'transparent';
  :hover {
    background: #0e3b51;
  }
  @media (max-width: ${responsive.mobile}px) {
    background: transparent;
    :hover {
      background: transparent;
    }
  }
`;

const SCajaNotificacion = styled(({ push, ...rest }) => <div {...rest} />)`
  flex: 0 0 44px;
  height: 44px;
  background-color: transparent;
  align-self: baseline;
  margin-left: ${({ push }) => (push ? 'auto' : undefined)};
  button {
    background-color: transparent;
  }

  /*necesario poner width y height para el elemento svg para IE11*/
  svg {
    width: 60px;
    height: 60px;
    fill: #1c4183;
  }
  @media (max-width: 768px) {
    flex: 0 0 50px;
    height: 50px;
    background-color: white;
    /*necesario poner width y height para el elemento svg para IE11*/
    svg {
      width: 50px;
      height: 50px;
      fill: #1c4183;
    }
  }
  @media (min-width: 769px) and (max-width: 1024px) {
    svg {
      fill: #fff;
    }
  }
`;

const SCajaDescargas = styled.div`
  flex: 0 0 auto;
  width: 44px;
  height: 44px;

  align-self: center;
  @media print {
    display: none;
  }
`;

const StyledA = styled.button`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  background: transparent;
  &&.focus-visible {
    outline: 2px solid #222222 !important;
    outline-offset: -2px !important;
  }
`;

const TitleSectionSelected = styled.div`
  background: #fff;
`;

const NombreSection = styled.p`
  font-size: 20px;
  font-weight: 500;
  color: #112f50;
  padding: 10px;
`;
export default Resultados;
